import React, {useEffect, useMemo, useRef, useState} from "react";
import BodyContainer from "../Common/BodyContainer/BodyContainer";
import {getSalesEmployeeByBrand} from "../HTTPS/apisInterface";
import { employeeMatchesSalespersonSlug } from "../../utils/salespersonSlug";
import Paper from '@material-ui/core/Paper';
import {KeyboardDatePicker} from "@material-ui/pickers";
import Grid from "@material-ui/core/Grid";
import Typography from '@material-ui/core/Typography';
import Button from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";
import CircleProgressWithLabel from "../Common/CircleProgress/CircleProgressWithLabel";
import Lottie from "react-lottie";
import busyAnimation from './busyschedule.json';
import {carBrands} from "../../AppConstants";
import Card from "@material-ui/core/Card";
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

const defaultOptions = {
	loop: true,
	autoplay: true,
	animationData: busyAnimation,
	rendererSettings: {
		preserveAspectRatio: "xMidYMid slice",
	},
};

const useDatePickerStyles = makeStyles((theme) => ({
	datePickerWrapper: {
		width: "100%",
		[theme.breakpoints.up("md")]: {
			width: "25%",
		},
	},
	salespersonButton: {
		width: "100%",
		[theme.breakpoints.up("sm")]: {
			width: "auto",
			minWidth: 220,
		},
	},
	salespersonSection: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: 16,
		flexDirection: "column",
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
		},
	},
	salespersonDropdownBlock: {
		display: "block",
		flex: "1 1 auto",
		order: 2,
		width: "100%",
		[theme.breakpoints.up("sm")]: {
			order: 1,
			width: "auto",
		},
	},
	salespersonAvatarBlock: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		flexShrink: 0,
		order: 1,
		alignSelf: "center",
		[theme.breakpoints.up("sm")]: {
			order: 2,
			marginTop: -40,
			alignSelf: "auto",
		},
	},
	salespersonAvatarCircle: {
		width: 80,
		height: 80,
		borderRadius: "50%",
		overflow: "hidden",
		backgroundColor: "#e0e0e0",
		border: "3px solid #fff",
		boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
		marginTop: 0,
		// When a salesperson is selected, make the avatar a bit larger on mobile.
		[theme.breakpoints.down("sm")]: {
			width: 150,
			height: 150,
		},
	},
	salespersonPaper: {
		overflow: "hidden",
		[theme.breakpoints.up("sm")]: {
			overflow: "visible",
		},
	},
	timeslotGrid: {
		display: "grid",
		width: "100%",
		gap: "15px",
		gridTemplateColumns: "repeat(3, 1fr)",
		[theme.breakpoints.up("sm")]: {
			gridTemplateColumns: "repeat(6, 1fr)",
		},
	},
	confirmButton: {
		width: "100%",
		[theme.breakpoints.up("sm")]: {
			width: "auto",
		},
	},
	datePickerIconButton: {
		"&:hover": {
			backgroundColor: "transparent",
		},
	},
	datePickerKeyboardButton: {
		"&:hover": {
			backgroundColor: "transparent",
		},
	},
}));

const useTimeSlotButtonStyles = makeStyles({
	timeSlotButton: {
		backgroundColor: "white",
		border: "1px solid rgba(0, 0, 0, 0.23)",
		color: "var(--timeslot-brand-primary)",
		boxShadow: "none",
		padding: "12px 20px",
		fontWeight: 600,
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.23)",
			borderColor: "var(--timeslot-brand-primary)",
			color: "white",
			boxShadow: "none",
		},
	},
	timeSlotButtonSelected: {
		backgroundColor: "var(--timeslot-brand-primary)",
		border: "1px solid var(--timeslot-brand-primary)",
		color: "white",
		boxShadow: "none",
		padding: "12px 20px",
		fontWeight: 600,
		"&:hover": {
			backgroundColor: "var(--timeslot-brand-selected)",
			borderColor: "var(--timeslot-brand-selected)",
			color: "white",
			boxShadow: "none",
		},
	},
});

// Base path for form navigation (strip trailing /booking so we can append it when navigating to form)
function getBookingBasePath(pathname) {
	return pathname.replace(/\/booking\/?$/, '') || pathname;
}

// Parse YYYY-MM-DD query param into a local Date (or null if invalid)
function parseDateParam(dateStr) {
	if (!dateStr || typeof dateStr !== 'string') return null;
	const parts = dateStr.trim().split('-').map(Number);
	if (parts.length !== 3) return null;
	const [y, m, d] = parts;
	if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
	if (m < 1 || m > 12 || d < 1 || d > 31) return null;
	const date = new Date(y, m - 1, d);
	if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
	return date;
}

function tomorrowLocalDate() {
	const d = new Date();
	d.setDate(d.getDate() + 1);
	return d;
}

export default function BookingSystem(props) {
	const history = useHistory();
	const location = useLocation();
	const { initialSalespersonSlug } = props;
	const brandPrimary = props.brand?.name === carBrands.jaguar.title ? 'rgb(158, 27, 50)' : 'rgb(0, 0, 0)';
	const brandSelected = props.brand?.name === carBrands.jaguar.title ? 'rgb(120, 20, 40)' : 'rgb(60, 60, 60)';
	const fontFamily = props.brand?.name === carBrands.jaguar.title ? 'Jaguar-Secondary' : 'LandRover-Secondary';
	const timeSlotClasses = useTimeSlotButtonStyles();
	const datePickerClasses = useDatePickerStyles();
	const [employees, setEmployees] = useState([]);
	const [selectedSalesperson, setSelectedSalesperson] = useState(null); // null = no preference, else employee email
	const [alert, setAlertView] = useState("hidden");
	const [selectedDate, setSelectedDate] = useState(() => {
		const fromQuery = parseDateParam(new URLSearchParams(location.search).get('date'));
		if (fromQuery) return fromQuery;
		if (props.startCalendarOnNextDay) return tomorrowLocalDate();
		return new Date();
	});
	const [loading, setLoading] = useState(true);
	const [message, setMessage] = useState({ message: "", status: "success" });
	const [stopLoadingProgressBar, setStopLoadingProgressBar] = useState(false);
	const [disabledDatePicker, setDisabledDatePicker] = useState(true);
	const [loadingText] = useState("Beschikbaarheden verkopers ophalen");
	const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
	const [salespersonModalOpen, setSalespersonModalOpen] = useState(false);
	const datePickerInputRef = useRef(null);
	const handleDateChange = (date) => {
		if (disabledDatePicker) {
			return;
		}
		if (date === "Invalid Date" || date === null || typeof date === 'undefined') {
			return;
		}
		if (date.toISOString().split("T")[0] < new Date(Date.now()).toISOString().split("T")[0]) {
			setMessage({ message: "Date can't be selected", status: "error" });
			setAlertView("visible");
			setSelectedDate(date);
			setTimeout(() => setAlertView("hidden"), 1500);
			setLoading(false);
			return;
		}
		setDisabledDatePicker(true);
		setLoading(true);
		setStopLoadingProgressBar(false);
		props.reactGA.event({
			category: "Booking Calendar",
			action: "Change Date",
			label: date.toLocaleDateString("nl-BE", { weekday: "short", year: "numeric", month: "short", day: "numeric" }),
		});
		setMessage({ message: `Date changed to ${date.getDate()}/${date.getMonth() + 1}/${date.getUTCFullYear()}`, status: "success" });
		setAlertView("visible");
		setTimeout(() => setAlertView("hidden"), 1500);
		setSelectedDate(date);
	};
	const firstUpdate = useRef(true);
	// Fetch employees whenever location, condition, or selected date changes (single source of truth)
	const dateKey = selectedDate ? `${selectedDate.getFullYear()}-${('0'+(selectedDate.getMonth()+1)).slice(-2)}-${('0'+selectedDate.getDate()).slice(-2)}` : '';
	useEffect(()=>{
		if (!props.brand?.currentLocationFullName || !dateKey) return;
		const formattedDate = dateKey;

		getSalesEmployeeByBrand(props.brand.currentLocationFullName, formattedDate, props.brandCondition).then(response=>{
			const list = Array.isArray(response) ? response : [];
			list.forEach(item=>{
				let available = false;
				(item.period || []).forEach(period=>{
					if(period.available === true){
						available = true;
					}
				});
				item.isAvailable = available;
			});
			setEmployees(list);
			setStopLoadingProgressBar(true);
			setDisabledDatePicker(false);
			setLoading(false);
		}).catch(()=>{
			setEmployees([]);
			setStopLoadingProgressBar(true);
			setDisabledDatePicker(false);
			setLoading(false);
		});
	},[props.brand?.currentLocationFullName, props.brandCondition, dateKey])

	// When URL has ?date=YYYY-MM-DD, keep date picker in sync (e.g. direct link with date)
	useEffect(() => {
		const dateFromQuery = parseDateParam(new URLSearchParams(location.search).get('date'));
		if (dateFromQuery) setSelectedDate(dateFromQuery);
	}, [location.search]);

	useEffect(() => {
		if(firstUpdate.current){
			firstUpdate.current = false;
		}
		else{
			setTimeout(()=>{setLoading(!stopLoadingProgressBar)},200);
		}
	}, [stopLoadingProgressBar]);

	// When opening with a salesperson slug in the URL, pre-select that employee once list is loaded
	useEffect(() => {
		if (!initialSalespersonSlug || !employees || employees.length === 0) return;
		const match = employees.find((employee) =>
			employeeMatchesSalespersonSlug(employee, initialSalespersonSlug)
		);
		if (match) setSelectedSalesperson(match.email);
	}, [initialSalespersonSlug, employees]);

	// Clear selected timeslot when date, salesperson or employees change
	useEffect(() => {
		setSelectedTimeSlot(null);
	}, [selectedDate, selectedSalesperson, employees]);

	// When no salesperson selected: merge all available slots; each slot maps to list of employees who have it
	const mergedTimeSlots = useMemo(() => {
		if (!employees || employees.length === 0) return [];
		const slotToEmployees = new Map();
		employees.forEach(emp => {
			if (!emp.period) return;
			emp.period.forEach(p => {
				if (!p.available) return;
				if (!slotToEmployees.has(p.timeSlot)) slotToEmployees.set(p.timeSlot, []);
				slotToEmployees.get(p.timeSlot).push(emp);
			});
		});
		const list = Array.from(slotToEmployees.entries()).map(([timeSlot, emps]) => ({ timeSlot, employees: emps }));
		list.sort((a, b) => {
			const [hA, mA] = a.timeSlot.split(':').map(Number);
			const [hB, mB] = b.timeSlot.split(':').map(Number);
			return (hA * 60 + mA) - (hB * 60 + mB);
		});
		return list;
	}, [employees]);

	const employeesToShow = useMemo(() => {
		if (!employees) return [];
		if (selectedSalesperson == null) return []; // no single employee when showing all slots
		return employees.filter(e => e.email === selectedSalesperson);
	}, [employees, selectedSalesperson]);

	// Build slot list for selected employee (same structure as Employee: dedupe, sort, fill 30-min intervals)
	const selectedEmployeeSlots = useMemo(() => {
		const emp = employeesToShow[0];
		if (!emp || !emp.period || emp.period.length === 0) return [];
		const slotMap = new Map();
		emp.period.forEach(item => {
			if (!slotMap.has(item.timeSlot)) slotMap.set(item.timeSlot, item);
		});
		let uniquePeriods = Array.from(slotMap.values());
		uniquePeriods.sort((a, b) => {
			const timeA = a.timeSlot.split(':').map(Number);
			const timeB = b.timeSlot.split(':').map(Number);
			return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
		});
		const firstSlot = uniquePeriods[0].timeSlot;
		const lastSlot = uniquePeriods[uniquePeriods.length - 1].timeSlot;
		const [firstHour, firstMinute] = firstSlot.split(':').map(Number);
		const [lastHour, lastMinute] = lastSlot.split(':').map(Number);
		const filledSlots = [];
		const existingSlotMap = new Map(uniquePeriods.map(s => [s.timeSlot, s]));
		let currentHour = firstHour, currentMinute = firstMinute;
		const endTotalMinutes = lastHour * 60 + lastMinute;
		while (true) {
			const totalMinutes = currentHour * 60 + currentMinute;
			if (totalMinutes > endTotalMinutes) break;
			const timeSlotStr = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
			filledSlots.push(existingSlotMap.has(timeSlotStr) ? existingSlotMap.get(timeSlotStr) : { timeSlot: timeSlotStr, available: false });
			currentMinute += 30;
			if (currentMinute >= 60) { currentMinute = 0; currentHour++; }
		}
		return filledSlots;
	}, [employeesToShow]);

	const handleBookMergedSlot = (timeSlot, employeesForSlot) => {
		const chosen = employeesForSlot[Math.floor(Math.random() * employeesForSlot.length)];
		props.reactGA.event({
			category: "Booking Calendar",
			action: "Select Timeslot",
			label: timeSlot
		});
		const basePath = getBookingBasePath(history.location.pathname);
		history.push(`${basePath}/booking${history.location.search}`, {
			carData: null,
			employee: chosen,
			brand: props.brand,
			location: props.location,
			currentDate: selectedDate,
			timeSlot
		});
	};

	const handleBookEmployeeSlot = (employee, timeSlot) => {
		props.reactGA.event({
			category: "Booking Calendar",
			action: "Select Timeslot",
			label: timeSlot
		});
		const basePath = getBookingBasePath(history.location.pathname);
		history.push(`${basePath}/booking${history.location.search}`, {
			carData: null,
			employee,
			brand: props.brand,
			location: props.location,
			currentDate: selectedDate,
			timeSlot
		});
	};

	const handleConfirmChoice = () => {
		if (!selectedTimeSlot) return;
		if (selectedSalesperson == null) {
			const entry = mergedTimeSlots.find((s) => s.timeSlot === selectedTimeSlot);
			if (entry) handleBookMergedSlot(entry.timeSlot, entry.employees);
		} else {
			if (employeesToShow.length > 0) handleBookEmployeeSlot(employeesToShow[0], selectedTimeSlot);
		}
	};

	return (
		<React.Fragment>
			<BodyContainer
				{...props}
				childComponent={
				<div className="col">
					 <div style={{ marginTop: "15px", display: "flex", flexDirection: "column", gap: "15px" }}>
						 <Paper style={{ padding: 16 }}>
							<Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Main' : 'landrover-font-Main'} gutterBottom variant="h6">Kies een datum</Typography>
								<div className={datePickerClasses.datePickerWrapper}>
									<KeyboardDatePicker
										fullWidth
										autoOk={true}
										disabled={disabledDatePicker}
										minDate={new Date()}
										allowKeyboardControl={false}
										disablePast={true}
										variant="inline"
										format="dd/MM/yyyy"
										margin="normal"
										id="date-picker-inline"
										value={selectedDate}
										onChange={handleDateChange}
										inputRef={datePickerInputRef}
										keyboardIcon={<ArrowDropDownIcon style={{ color: 'rgba(0, 0, 0, 0.87)', fontSize: 28 }} />}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<IconButton
														aria-label="open calendar"
														disabled={disabledDatePicker}
														onClick={() => datePickerInputRef.current?.focus()}
														edge="start"
														size="small"
														disableRipple
														className={datePickerClasses.datePickerIconButton}
													>
														<CalendarTodayIcon />
													</IconButton>
												</InputAdornment>
											),
										}}
										KeyboardButtonProps={{
											'aria-label': 'change date',
											disableRipple: true,
											className: datePickerClasses.datePickerKeyboardButton,
										}}
									/>
								</div>
						 </Paper>
						 <Paper className={datePickerClasses.salespersonPaper} style={{ marginBottom: '15px', padding: 16 }}>
							 <div className={datePickerClasses.salespersonSection}>
								 <div className={datePickerClasses.salespersonDropdownBlock}>
								 <Typography className={props.brand.name === carBrands.jaguar.title ? "jaguar-font-Main" : "landrover-font-Main"} gutterBottom variant="h6">Kies een verkoper</Typography>
								 <Button
									 variant="outlined"
									 onClick={() => setSalespersonModalOpen(true)}
									 disabled={employees === null || (employees && employees.length === 0)}
									 className={datePickerClasses.salespersonButton}
									 style={{ fontFamily: props.brand.name === carBrands.jaguar.title ? 'Jaguar-Secondary' : 'LandRover-Secondary', justifyContent: 'flex-start', textTransform: 'none' }}
								 >
									 {selectedSalesperson == null ? 'Iedereen' : (employees && employees.find(e => e.email === selectedSalesperson))?.name ?? selectedSalesperson}
								 </Button>
								 </div>
								 {selectedSalesperson != null && employees && (() => {
									 const emp = employees.find(e => e.email === selectedSalesperson);
									 if (!emp) return null;
									 const imgUrl = emp.image && (typeof emp.image === 'string' ? emp.image : emp.image?.url);
									 return (
										 <div key={emp.email} className={datePickerClasses.salespersonAvatarBlock}>
											 <div className={datePickerClasses.salespersonAvatarCircle}>
												 {imgUrl ? <img src={imgUrl} alt={emp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 24, color: '#666' }}>{emp.name ? emp.name.charAt(0) : '?'}</span></div>}
											 </div>
											 <Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Secondary' : 'landrover-font-Secondary'} style={{ marginTop: 8, fontWeight: 500, textAlign: 'center' }}>{emp.name}</Typography>
										 </div>
									 );
								 })()}
							 </div>
								 <Dialog open={salespersonModalOpen} onClose={() => setSalespersonModalOpen(false)} aria-labelledby="salesperson-dialog-title" maxWidth="sm" fullWidth>
									 <DialogTitle id="salesperson-dialog-title" style={{paddingBottom: "0px"}} className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Main' : 'landrover-font-Main'}>Kies een verkoper</DialogTitle>
									 <DialogContent style={{paddingBottom: "20px"}}>
										 <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
											 <div
												 onClick={() => { setSelectedSalesperson(null); setSalespersonModalOpen(false); }}
												 style={{
													 display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer', borderRadius: 8,
													 border: '2px solid', borderColor: selectedSalesperson == null ? (props.brand.name === carBrands.jaguar.title ? 'rgb(158, 27, 50)' : 'rgb(0 0 0)') : '#e0e0e0', backgroundColor: selectedSalesperson == null ? (props.brand.name === carBrands.jaguar.title ? 'rgba(158, 27, 50, 0.08)' : 'rgba(0,0,0,0.06)') : 'transparent'
												 }}
											 >
												 <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
													 <span style={{ fontSize: 20, color: '#666' }}>—</span>
												 </div>
												 <Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Secondary' : 'landrover-font-Secondary'} style={{ fontWeight: 500 }}>Geen voorkeur</Typography>
											 </div>
											 {employees && employees.map((emp) => {
												 const imgUrl = emp.image && (typeof emp.image === 'string' ? emp.image : emp.image?.url);
												 return (
													 <div
														 key={emp.email}
														 onClick={() => { setSelectedSalesperson(emp.email); setSalespersonModalOpen(false); }}
														 style={{
															 display: 'flex', alignItems: 'center', gap: 12, padding: 12, cursor: 'pointer', borderRadius: 8,
															 border: '2px solid', borderColor: selectedSalesperson === emp.email ? (props.brand.name === carBrands.jaguar.title ? 'rgb(158, 27, 50)' : 'rgb(0 0 0)') : '#e0e0e0', backgroundColor: selectedSalesperson === emp.email ? (props.brand.name === carBrands.jaguar.title ? 'rgba(158, 27, 50, 0.08)' : 'rgba(0,0,0,0.06)') : 'transparent'
														 }}
													 >
														 <div style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: '#e0e0e0' }}>
															 {imgUrl ? <img src={imgUrl} alt={emp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 18, color: '#666' }}>{emp.name ? emp.name.charAt(0) : '?'}</span></div>}
														 </div>
														 <Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Secondary' : 'landrover-font-Secondary'} style={{ fontWeight: 500 }}>{emp.name}</Typography>
													 </div>
												 );
											 })}
										 </div>
									 </DialogContent>
								 </Dialog>
						 </Paper>
					 </div>
					 { loading ? <div style={{display:'flex',alignItems:'center',flexDirection:"column",marginTop:'80px'}}><h5 style={{fontFamily:`${ props && props.brand && props.brand.name === carBrands.jaguar.title ? 'Jaguar-Secondary':'LandRover-Secondary'}`}}>{loadingText}</h5><CircleProgressWithLabel stopProgress={stopLoadingProgressBar} value={0} /></div>:
							<Grid justifyContent="flex-start" alignItems="baseline" container spacing={4}>
							{selectedSalesperson == null ? (
								// No salesperson selected: show all timeslots in one list; on book, randomly pick an employee
								employees !== null && mergedTimeSlots.length > 0 && (
									<Grid item xs={12}>
										<div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
										<Card style={{ padding: 16 }}>
											<div style={{ display: 'block', '--timeslot-brand-primary': brandPrimary, '--timeslot-brand-selected': brandSelected }}>
												<Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Main' : 'landrover-font-Main'} gutterBottom variant="h6">Beschikbare tijdsloten</Typography>
												<Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Secondary' : 'landrover-font-Secondary'} variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>Kies een tijdslot. Een verkoper wordt automatisch toegewezen.</Typography>
												<div className={datePickerClasses.timeslotGrid}>
													{mergedTimeSlots.map(({ timeSlot, employees: emps }) => (
														<div key={timeSlot} className="time" style={{ display: 'flex', alignItems: 'center', margin: '4px 0' }}>
															<Button
																onClick={() => setSelectedTimeSlot(selectedTimeSlot === timeSlot ? null : timeSlot)}
																className={selectedTimeSlot === timeSlot ? timeSlotClasses.timeSlotButtonSelected : timeSlotClasses.timeSlotButton}
																style={{ fontFamily, width: '100%' }}
																color="primary"
																variant="contained"
															>{timeSlot}</Button>
														</div>
													))}
												</div>
											</div>
										</Card>
										<Card style={{ padding: 16 }}>
											<div style={{ display: 'block' }}>
												<Button
													onClick={handleConfirmChoice}
													className={datePickerClasses.confirmButton}
													style={{fontFamily: props.brand.name === carBrands.jaguar.title ? 'Jaguar-Secondary' : 'LandRover-Secondary', backgroundColor: props.brand.name === carBrands.jaguar.title ? 'rgb(158, 27, 50)' : 'rgb(0 0 0)' }}
													color="primary"
													variant="contained"
												>Bevestig keuze</Button>
											</div>
										</Card>
										</div>
									</Grid>
								)
							) : (
								// Salesperson selected: same card styling as "Geen voorkeur", show their slots only
								employeesToShow.length > 0 && (
									<Grid item xs={12}>
										<div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
										<Card style={{ padding: 16 }}>
											<div style={{ display: 'block', '--timeslot-brand-primary': brandPrimary, '--timeslot-brand-selected': brandSelected }}>
												<Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Main' : 'landrover-font-Main'} gutterBottom variant="h6">Beschikbare tijdsloten</Typography>
												{(() => {
													const availableSlots = selectedEmployeeSlots.filter((item) => item.available);
													if (availableSlots.length > 0) {
														return (
															<div className={datePickerClasses.timeslotGrid}>
																{availableSlots.map((item) => (
																	<div key={item.timeSlot} className="time" style={{ display: 'flex', alignItems: 'center', margin: '4px 0' }}>
																		<Button
																			onClick={() => setSelectedTimeSlot(selectedTimeSlot === item.timeSlot ? null : item.timeSlot)}
																			className={selectedTimeSlot === item.timeSlot ? timeSlotClasses.timeSlotButtonSelected : timeSlotClasses.timeSlotButton}
																			style={{ fontFamily, width: '100%' }}
																			color="primary"
																			variant="contained"
																		>{item.timeSlot}</Button>
																	</div>
																))}
															</div>
														);
													}
													return (
														<Typography className={props.brand.name === carBrands.jaguar.title ? 'jaguar-font-Secondary' : 'landrover-font-Secondary'} style={{ textAlign: 'center' }}>
															Alle tijdsloten zijn geboekt. Kies een andere datum of verkoper.
														</Typography>
													);
												})()}
											</div>
										</Card>
										<Card style={{ padding: 16 }}>
											<div style={{ display: 'block' }}>
												<Button
													onClick={handleConfirmChoice}
													className={datePickerClasses.confirmButton}
													style={{ fontFamily: props.brand.name === carBrands.jaguar.title ? 'Jaguar-Secondary' : 'LandRover-Secondary', backgroundColor: props.brand.name === carBrands.jaguar.title ? 'rgb(158, 27, 50)' : 'rgb(0 0 0)' }}
													color="primary"
													variant="contained"
												>Bevestig keuze</Button>
											</div>
										</Card>
										</div>
									</Grid>
								)
							)}
								{employees !== null && employees.length === 0 && (
									<div className="container" style={{ marginLeft: "auto", marginRight: "auto" }}>
										<p style={{ fontWeight: "bolder", textAlign: "center", marginTop: "20px" }}>Het lijkt er op dat alle afspraken vandaag geboekt zijn. Probeer op een later tijdstip.</p>
										<Lottie options={defaultOptions} height={400} width={400} />
									</div>
								)}
								{employees !== null && employees.length > 0 && selectedSalesperson == null && mergedTimeSlots.length === 0 && (
									<div className="container" style={{ marginLeft: "auto", marginRight: "auto" }}>
										<p style={{ fontWeight: "bolder", textAlign: "center", marginTop: "20px" }}>Geen beschikbare tijdsloten voor deze datum.</p>
									</div>
								)}
						</Grid>}
				</div>
			}
			/>
			<Alert
				style={{
					width: "fit-content",
					position: "absolute",
					top: "10px",
					left: 0,
					right: 0,
					marginLeft: "auto",
					marginRight: "auto",
					visibility: alert,
				}}
				severity={message.status}
			>
				{message.message}
			</Alert>
		</React.Fragment>
	);
}
